import { zodResolver } from "@hookform/resolvers/zod";
import { createJob, getJob, getRepairReview, type RepairReviewResponse } from "@stryker/core/api/jobs";
import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { Job, LocalFile } from "@stryker/core/types/jobs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { uniqueBy } from "remeda";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";
import RepairReviewModal from "@/components/job/RepairReviewModal";
import { Button, useToast } from "@/components/ui";
import { Spinner } from "@/components/ui/spinner";
import { JOB_TYPES } from "@/constants/job";
import { additionalNotesQueryOptions } from "@/hooks/useAdditionalNotes";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { useGetConfig } from "@/hooks/useConfigOptionsAgent";
import useLocationQuery from "@/hooks/useLocationQuery";
import useScreens from "@/hooks/useScreens";
import { selectStateForJob } from "@/pages/admin/compositeJob/helpers";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { createInitialState } from "@/utils/mappings/composite";
import { AiChat, ProMode } from "../components";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { DocumentUploader } from "./DocumentUploader";
import { JobFormSections } from "./JobFormSections";
import { JobHeader } from "./JobHeader";
import { ModeToggle } from "./ModeToggle";

const jobNameSchema = z.object({
  job_name: z.string().max(30).optional(),
});

type JobNameSchema = z.infer<typeof jobNameSchema>;

type MergedDoc = Array<LocalFile | Job["attached_documents"][number]>;

type CreationMode = "standard" | "ai" | "pro";

type PreparedData = {
  submitData: FormData | Record<string, unknown>;
  displayData: Record<string, unknown>;
  isMultipart: boolean;
};

const POLAR_ANSWER_KEYS = [
  "do_you_have_ut_reading_info",
  "calculate_at_worst_case",
  "is_ut_reading_less_than_04",
  "per_code_line_leaking_do_you_know_diameter",
  "component_leaking",
  "is_pinhole_leak",
  "component_patched_or_repaired",
  "is_component_shutdown_during_repair",
  "add_extraLayers",
  "do_you_wish_to_answer_more_questions",
  "is_component_buried",
  "design_axial_loads",
  "is_component_straight_line",
  "do_you_want_to_take_into_account_any_additional_forces_and_moments",
  "do_you_want_to_design_for_external_loads_or_vacuum",
  "do_you_want_to_design_for_constant_pressure_cycles",
  "are_the_expected_cycles_more_than_7000_over_design_life",
  "do_you_want_to_design_for_continous_long_term_strains",
  "do_you_want_to_completely_exclude_the_components_strength_for_design",
];

const normalizePolarAnswers = (payload: Record<string, unknown>) => {
  const normalizedPayload = { ...payload };
  POLAR_ANSWER_KEYS.forEach((key) => {
    const value = normalizedPayload[key];
    if (value === "yes") {
      normalizedPayload[key] = true;
    } else if (value === "no") {
      normalizedPayload[key] = false;
    }
  });
  return normalizedPayload;
};

const sanitizeNewNotes = (payload: Record<string, unknown>) => {
  if (!Array.isArray(payload.new_notes)) {
    return payload;
  }

  const filteredNotes = (payload.new_notes as Array<{ custom_name_of_note?: string; custom_note?: string }>).filter(
    (note) => !!note.custom_name_of_note && !!note.custom_note
  );

  if (!filteredNotes.length) {
    const { new_notes, ...rest } = payload;
    return rest;
  }

  return {
    ...payload,
    new_notes: filteredNotes,
  };
};

export default function CreateUpdateJob() {
  const [documents, setDocuments] = useState<MergedDoc>([]);
  const [referenceDocuments, setReferenceDocuments] = useState<MergedDoc>([]);
  const [isPositionFixed, setIsPositionFixed] = useState(false);
  const [plantRepairIdentifier, setPlantRepairIdentifier] = useState<string | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairReviewData, setRepairReviewData] = useState<RepairReviewResponse | null>(null);
  const [creationMode, setCreationMode] = useState<CreationMode>("standard");
  const [proPreparedData, setProPreparedData] = useState<PreparedData | undefined>(undefined);
  const [aiPayload, setAiPayload] = useState<Record<string, unknown> | undefined>(undefined);
  const [isInitialHydrationDone, setIsInitialHydrationDone] = useState(false);

  const { data: profile } = useQuery({
    ...profileQueryOptions(),
    select: (response) => ({
      id: response.id,
      canRunCalculations: profileSelectors.canRunCalculations(response),
    }),
  });

  const configHook = useConfigOptions();
  const { data: { additionalNotes = [] } = {} } = useQuery({
    ...additionalNotesQueryOptions("composite"),
    select: (data) => ({
      additionalNotes: data?.additional_notes ?? [],
    }),
  });
  const { data: aiConfigOptions, isLoading: isAiConfigLoading } = useGetConfig();
  const configOptions = {
    repairSystemOptions: configHook.repairSystemOptions ?? [],
    surfacePreparationOptions: configHook.surfacePreparationOptions ?? [],
    temperatureUnitsOptions: configHook.temperatureUnitsOptions ?? [],
    measureUnitOptions: configHook.measureUnitOptions ?? [],
    npsOptions: configHook.npsOptions ?? [],
    materialOptions: configHook.materialOptions ?? [],
    kitOptions: configHook.allKits ?? [],
    jobFlagsOptions: configHook.jobFlagsOptions ?? [],
    usersOptions: configHook.usersOptions ?? [],
    plantOptions: configHook.plantOptions ?? [],
    branchOptions: configHook.branchOptions ?? [],
    repairItemsOptions: configHook.repairItemsOptions ?? [],
    repairUnitTypesOptions: configHook.repairUnitTypesOptions ?? [],
  };

  // Add loading check for config
  const isConfigLoading = configHook.repairSystemOptions === undefined || isAiConfigLoading;

  const userId = String(profile?.id ?? "user-placeholder-123");
  const canRunCalculations = profile?.canRunCalculations;

  const isTabletOrAbove = useScreens("md", "up");
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useLocationQuery();
  const jobName = query.get("jobName");
  const { id: draftJobId } = useParams();
  const duplicateJobId = query.get("jobId");
  const jobId = window.location.href.includes("draft-edit") ? draftJobId : duplicateJobId;

  const { data: job, isLoading: isJobLoading } = useQuery({
    queryKey: [QueryKeys.Job, jobId],
    queryFn: () => getJob(jobId as string),
    enabled: !!jobId,
  });

  useEffect(() => {
    setIsInitialHydrationDone(!jobId);
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !job || isInitialHydrationDone) {
      return;
    }
    // we are using draft or copy
    if (job.attached_documents && !documents.some((d) => "link" in d)) {
      setDocuments((prevState) => uniqueBy([...prevState, ...job.attached_documents], (d) => d.name));
    }
    if (job.reference_documents && !referenceDocuments.some((d) => "link" in d)) {
      setReferenceDocuments((prevState) => uniqueBy([...prevState, ...job.reference_documents], (d) => d.name));
    }
    setIsInitialHydrationDone(true);
  }, [documents, isInitialHydrationDone, job, jobId, referenceDocuments]);

  const {
    areAllRequiredFieldsFilled,
    atLeastOneSectionFilled,
    dataForSubmit,
    resetState,
    setDefaultValues,
    setJobIdForDuplication,
    jobIdForDuplication,
    setJobDetails,
    setAdditionalJobDetails,
    toggleExpandableSection,
    setComponentDetails,
    setDefectDetails,
    setRepairOptions,
    setRepairKits,
    setDesignConditions,
    // UNUSED: setOperatingConditions is not used anywhere
    // setOperatingConditions,
    sections,
  } = useCUCompositeJobStore(useShallow(selectStateForJob));

  // Reset state when jobId changes (switching between jobs)
  useEffect(() => {
    if (jobId && jobIdForDuplication && jobId !== jobIdForDuplication) {
      // Switching from one job to another - reset state first
      resetState();
      setJobIdForDuplication(null);
    }
  }, [jobId, jobIdForDuplication, resetState, setJobIdForDuplication]);

  // For duplication state
  useEffect(() => {
    if (!jobId) {
      return;
    }
    if (job && !jobIdForDuplication) {
      const defaultValues = createInitialState(job);
      setDefaultValues(defaultValues);
      setJobIdForDuplication(jobId);
    }
  }, [job, jobId, jobIdForDuplication, setDefaultValues, setJobIdForDuplication]);

  // Reset state on component unmount (leaving the flow entirely)
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  useEffect(() => {
    const bodyElement = document.body;
    const topPos = bodyElement.getBoundingClientRect().top;

    if (topPos < -100 && !isPositionFixed) {
      setIsPositionFixed(true);
    } else if (topPos >= -100 && isPositionFixed) {
      setIsPositionFixed(false);
    }
  }, []);

  useEffect(() => {
    function handleScroll() {
      const bodyElement = document.body;
      const topPos = bodyElement.getBoundingClientRect().top;

      if (topPos < -100 && !isPositionFixed) {
        setIsPositionFixed(true);
      } else if (topPos >= -100 && isPositionFixed) {
        setIsPositionFixed(false);
      }
    }

    window.addEventListener("scroll", handleScroll, false);

    return () => {
      window.removeEventListener("scroll", handleScroll, false);
    };
  }, [isPositionFixed]);

  const {
    register,
    formState: { errors },
    handleSubmit,
    // UNUSED: getValues is never called
    // getValues,
  } = useForm<JobNameSchema>({
    resolver: zodResolver(jobNameSchema),
    values: {
      job_name: jobName ? decodeURIComponent(jobName) : "",
    },
  });

  const { mutate: mutateCreateJob, isPending: isPendingCreateJob } = useMutation({
    mutationFn: createJob,
    onError: (error: string) => {
      toast({ variant: "destructive", description: error?.toString() });
    },
    onSuccess: async (response) => {
      resetState();
      setProPreparedData(undefined);
      setAiPayload(undefined);
      await Promise.all([
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobsConfig] }),
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobsList] }),
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.Job, String(response.id)] }),
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobReport, String(response.id)] }),
      ]);
      navigate(`/jobs/${response.id}`);
    },
    onSettled: () => {
      setShowConfirmModal(false);
      setShowRepairModal(false);
    },
  });

  const { mutate: mutateGetRepairReview, isPending: isLoadingGetRepairReview } = useMutation({
    mutationFn: getRepairReview,
    onError: (error: string) => {
      toast({ variant: "destructive", description: error?.toString() });
    },
    onSuccess: (response) => {
      setRepairReviewData(response);
      setShowRepairModal(true);
    },
  });

  const isPending = isPendingCreateJob || isLoadingGetRepairReview;

  function getJobData(is_draft: boolean, payload?: Record<string, unknown>) {
    if (payload) {
      // For AI/pro mode: Use provided payload, clean it
      const cleanedPayload: Record<string, unknown> = {
        ...payload,
        job_name: plantRepairIdentifier || payload.job_name,
        is_draft,
        job_type: JOB_TYPES.composite,
        job_product_type: "composite",
        repair_system_max_temp: configHook.repairSystems?.find((r) => r.id == payload?.repair_system)
          ?.repair_system_max_temp,
      };
      const sanitizedPayload = sanitizeNewNotes(cleanedPayload);
      for (const key in sanitizedPayload) {
        if (
          sanitizedPayload[key] === "" ||
          (Array.isArray(sanitizedPayload[key]) && (sanitizedPayload[key] as unknown[]).length === 0) ||
          sanitizedPayload[key] === undefined ||
          sanitizedPayload[key] === null
        ) {
          delete sanitizedPayload[key];
        }
        if (
          key === "components_data" &&
          Array.isArray(sanitizedPayload[key]) &&
          (sanitizedPayload[key] as unknown[]).length === 1 &&
          typeof (sanitizedPayload[key] as unknown[])[0] === "object" &&
          (sanitizedPayload[key] as unknown[])[0] !== null &&
          Object.values((sanitizedPayload[key] as unknown[])[0] as Record<string, unknown>).every(
            (v: unknown) => v === null || v === "" || v === undefined
          )
        ) {
          delete sanitizedPayload[key];
        }
      }
      // No documents in AI flow, so return as JSON (non-multipart)
      return sanitizedPayload;
    }

    // Standard mode logic
    const payloadData = sanitizeNewNotes({
      job_name: plantRepairIdentifier,
      is_draft,
      job_type: JOB_TYPES.composite,
      job_product_type: "composite",
      ...dataForSubmit,
      repair_system_max_temp: configHook.repairSystems?.find((r) => r.id == dataForSubmit?.repair_system)
        ?.repair_system_max_temp,
    });

    const formData = new FormData();
    if (documents.length || referenceDocuments.length) {
      // Build multipart form data
      for (const property in payloadData) {
        const value = payloadData[property];
        if (Array.isArray(value) && value.filter(Boolean).length) {
          value.forEach((v: any) => {
            formData.append(property, JSON.stringify(v));
          });
        } else if (!Array.isArray(value)) {
          if (value !== null && typeof value === "object") {
            formData.append(property, JSON.stringify(value));
          } else {
            if (value !== undefined) {
              formData.append(property, typeof value !== "string" ? String(value) : value);
            }
          }
        }
      }

      documents.forEach((doc) => {
        if ("file" in doc) {
          formData.append("attached_documents", doc.file);
        } else {
          formData.append("attached_documents", doc.name);
        }
      });

      referenceDocuments.forEach((doc) => {
        if ("file" in doc) {
          formData.append("reference_documents", doc.file);
        } else {
          formData.append("reference_documents", doc.name);
        }
      });
      return formData;
    }
    return payloadData;
  }

  function handleCreateJob(is_draft = false) {
    let data: FormData | Record<string, unknown>;
    let isMultipart: boolean;

    if (creationMode === "pro" && proPreparedData) {
      data = proPreparedData.submitData;
      isMultipart = proPreparedData.isMultipart;
      if (data instanceof FormData) {
        data.set("is_draft", is_draft.toString());
      } else {
        (data as Record<string, unknown>).is_draft = is_draft;
      }
    } else if (creationMode === "ai" && aiPayload) {
      // AI mode: Use aiPayload, non-multipart (no docs)
      data = getJobData(is_draft, aiPayload);
      isMultipart = false;
    } else {
      // Standard mode
      data = getJobData(is_draft);
      isMultipart = !!documents.length || !!referenceDocuments.length;
    }

    mutateCreateJob({ data, isMultipart });
  }

  function handleSaveDraftClick() {
    if (creationMode === "standard") {
      handleSubmit((data) => {
        setPlantRepairIdentifier(data.job_name);
        handleCreateJob(true);
      })();
    } else {
      handleCreateJob(true);
    }
  }

  function handleRunCalculationsClick() {
    if (!canRunCalculations) {
      return;
    }
    if (creationMode === "standard") {
      handleSubmit((data) => {
        setPlantRepairIdentifier(data.job_name);
        const jobData = getJobData(false);
        const isMultipart = !!documents.length || !!referenceDocuments.length;
        mutateGetRepairReview({
          data: jobData,
          isMultipart,
        });
      })();
    } else {
      const jobData = getJobData(false, creationMode === "ai" ? aiPayload : undefined);
      const isMultipart = creationMode !== "ai" && (!!documents.length || !!referenceDocuments.length);
      mutateGetRepairReview({
        data: jobData,
        isMultipart,
      });
    }
  }

  function handleMobileRunCalculationsClick() {
    if (!canRunCalculations) {
      return;
    }
    if (creationMode === "standard") {
      handleSubmit((data) => {
        setPlantRepairIdentifier(data.job_name);
        const jobData = getJobData(false);
        mutateGetRepairReview({ data: jobData, isMultipart: !!documents.length || !!referenceDocuments.length });
        setShowConfirmModal(true);
      })();
    } else {
      const jobData = getJobData(false, creationMode === "ai" ? aiPayload : undefined);
      mutateGetRepairReview({
        data: jobData,
        isMultipart: creationMode !== "ai" && (!!documents.length || !!referenceDocuments.length),
      });
      setShowConfirmModal(true);
    }
  }

  function handleAiPreviewRequested(payload: Record<string, unknown>) {
    if (!canRunCalculations) {
      return;
    }
    if (!payload.job_number) {
      toast({ variant: "destructive", description: "AI payload missing job number. Please try again." });
      return;
    }
    setAiPayload(payload);
    setPlantRepairIdentifier((payload.job_name as string) || undefined);
    const jobData = getJobData(false, payload);
    mutateGetRepairReview({ data: jobData, isMultipart: false });
  }

  const activeDataForSubmit =
    creationMode === "pro" && proPreparedData
      ? proPreparedData.displayData
      : creationMode === "ai" && aiPayload
        ? aiPayload
        : dataForSubmit;

  const selectedRepairSystemName = configHook.repairSystems?.find(
    (r) => r.id === Number(activeDataForSubmit?.repair_system)
  )?.name;

  function handleModeSwitch(mode: CreationMode) {
    if (mode === "ai" && isConfigLoading) {
      toast({ variant: "destructive", description: "Config loading, please wait." });
      return;
    }
    setProPreparedData(undefined);
    setAiPayload(undefined);
    setCreationMode(mode);
  }

  const isToggleDisabled = isConfigLoading || isPendingCreateJob || isLoadingGetRepairReview;

  const hasJobNameError = creationMode === "standard" ? !!errors.job_name?.message : false;

  function handleProSaveDraft(submitData: FormData | Record<string, unknown>, isMultipart: boolean) {
    const payload = submitData instanceof FormData ? submitData : normalizePolarAnswers(submitData);
    if (payload instanceof FormData) {
      payload.set("is_draft", "true");
    } else {
      payload.is_draft = true;
    }
    mutateCreateJob({ data: payload, isMultipart });
  }

  function handleProRunCalculations(
    submitData: FormData | Record<string, unknown>,
    isMultipart: boolean,
    displayData: Record<string, unknown>
  ) {
    if (!canRunCalculations) {
      return;
    }
    const payload = submitData instanceof FormData ? submitData : normalizePolarAnswers(submitData);
    setProPreparedData({ submitData: payload, isMultipart, displayData });
    mutateGetRepairReview({ data: payload, isMultipart });
  }

  function handleDocumentAdd(file: LocalFile) {
    setDocuments((prevState) => [...prevState, file]);
  }

  function handleDocumentRemove(name: string) {
    setDocuments((prevState) => prevState.filter((p) => p.name !== name));
  }

  function handleReferenceDocumentAdd(file: LocalFile) {
    setReferenceDocuments((prevState) => [...prevState, file]);
  }

  function handleReferenceDocumentRemove(name: string) {
    setReferenceDocuments((prevState) => prevState.filter((p) => p.name !== name));
  }

  const shouldBlockUntilHydrated = !!jobId && (isJobLoading || !isInitialHydrationDone);

  if (shouldBlockUntilHydrated) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner svgClassName="h-20 w-20" />
      </div>
    );
  }

  return (
    <div className="relative pb-6">
      <JobHeader
        isPositionFixed={isPositionFixed}
        creationMode={creationMode}
        register={register}
        errors={errors}
        atLeastOneSectionFilled={atLeastOneSectionFilled}
        areAllRequiredFieldsFilled={areAllRequiredFieldsFilled}
        isPending={isPending}
        onSaveAsDraft={handleSaveDraftClick}
        onRunCalculations={handleRunCalculationsClick}
        canRunCalculations={canRunCalculations}
      />

      <ModeToggle creationMode={creationMode} isToggleDisabled={isToggleDisabled} onModeChange={handleModeSwitch} />

      <div>
        {creationMode === "standard" && (
          <>
            <JobFormSections
              temperatureUnitsOptions={configOptions.temperatureUnitsOptions}
              sections={sections}
              setJobDetails={setJobDetails}
              setAdditionalJobDetails={setAdditionalJobDetails}
              toggleExpandableSection={toggleExpandableSection}
              setDesignConditions={setDesignConditions}
              setComponentDetails={setComponentDetails}
              setDefectDetails={setDefectDetails}
              setRepairOptions={setRepairOptions}
              setRepairKits={setRepairKits}
            />
            <DocumentUploader
              documents={documents}
              referenceDocuments={referenceDocuments}
              onDocumentAdd={handleDocumentAdd}
              onDocumentRemove={handleDocumentRemove}
              onReferenceDocumentAdd={handleReferenceDocumentAdd}
              onReferenceDocumentRemove={handleReferenceDocumentRemove}
            />
          </>
        )}
        {creationMode === "standard" && !isTabletOrAbove && (
          <div className="mb-4 px-4">
            {!duplicateJobId && (
              <Button
                disabled={!atLeastOneSectionFilled || hasJobNameError || isPending}
                onClick={handleSaveDraftClick}
                className="mb-2 w-full"
                size="lg"
              >
                Save as draft
              </Button>
            )}
            {canRunCalculations && (
              <Button
                disabled={!areAllRequiredFieldsFilled || hasJobNameError || isPending}
                onClick={handleMobileRunCalculationsClick}
                className="w-full"
                size="lg"
              >
                Run calculations
              </Button>
            )}
          </div>
        )}
        {creationMode === "ai" && (
          <AiChat
            isFullPage={true}
            aiConfigOptions={aiConfigOptions}
            additionalNotes={additionalNotes}
            isConfigLoading={isConfigLoading}
            userId={userId}
            setDefaultValues={(values: Record<string, unknown>) =>
              setDefaultValues(values as Parameters<typeof setDefaultValues>[0])
            }
            toast={toast}
            navigate={navigate}
            sections={sections}
            onPreviewRequested={handleAiPreviewRequested}
            onClose={() => {
              setAiPayload(undefined);
              setCreationMode("standard");
            }}
          />
        )}
        {creationMode === "pro" && (
          <ProMode
            onCancel={() => setCreationMode("standard")}
            onSaveDraft={handleProSaveDraft}
            onRunCalculations={handleProRunCalculations}
            isPending={isPending}
            canRunCalculations={canRunCalculations}
          />
        )}
      </div>
      <RepairReviewModal
        showRepairModal={showRepairModal}
        setShowRepairModal={setShowRepairModal}
        dataForSubmit={activeDataForSubmit}
        setShowConfirmModal={setShowConfirmModal}
        setRepairReviewData={setRepairReviewData}
        repairReviewData={repairReviewData}
        selectedRepairSystemName={selectedRepairSystemName}
      />
      <ConfirmationDialog
        open={showConfirmModal}
        isPending={isPendingCreateJob}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => handleCreateJob(false)}
      />
    </div>
  );
}
